import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFileExcel,
  faInfoCircle,
  faExclamationTriangle,
  faTimesCircle,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import ApiService from "../../_services/ApiService";
import * as XLSX from "xlsx";
import "./student-marks.css";

function clamp(val, min, max) {
  if (val === "") return "";
  return Math.min(Math.max(Number(val), min), max);
}

const ABSENT_TOKENS = new Set(["A", "ABS", "ABSENT"]);
const ABSENT_PREFIXES = ["A", "AB", "ABS", "ABSE", "ABSEN", "ABSENT"];

function normalizeToken(value) {
  return typeof value === "string" ? value.trim().toUpperCase() : "";
}

function isAbsentToken(value) {
  return ABSENT_TOKENS.has(normalizeToken(value));
}

function isAbsentPrefix(value) {
  return ABSENT_PREFIXES.includes(normalizeToken(value));
}

export default function StudentMarks({ testId, batchId, onClose, isViewOnlyMode = false }) {
  const [students, setStudents] = useState([]);
  const [testMeta, setTestMeta] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cellStatus, setCellStatus] = useState({});
  const [totals, setTotals] = useState({});
  const [lockStatus, setLockStatus] = useState(null);
  const [lockMessage, setLockMessage] = useState("");
  const [isOnlineTest, setIsOnlineTest] = useState(false);
  const [testSections, setTestSections] = useState([]);
  const [parentSectionIds, setParentSectionIds] = useState([]);
  const [leafSections, setLeafSections] = useState([]);
  const [invalidCells, setInvalidCells] = useState({});
  const [testLockConfig, setTestLockConfig] = useState(null);
  const inputRefs = useRef({});
  const [hasEditPermission, setHasEditPermission] = useState(false);

  useEffect(() => {
    if (!testId || !batchId) return;
    setLoading(true);
    setError("");
    setStudents([]);
    setLeafSections([]);
    setLockStatus(null);
    setLockMessage("");
    ApiService.checkTestLock(testId, batchId)
      .then((lockResp) => {
        const locked = lockResp.data?.testLocked;
        const isOnlineTest = lockResp.data?.isOnlineTest;
        if (isOnlineTest) {
          setIsOnlineTest(true);
        }
        const hasPermission = lockResp.data?.hasTestDataEntryEditPermission ?? false;
        setTestLockConfig(lockResp.data || {});
        setLockStatus(locked);
        setLockMessage(lockResp.data?.testLockedMessage || lockResp.data?.lockMessage || "Test is locked.");

        if (isViewOnlyMode) {
          setHasEditPermission(false);
          return Promise.all([
            ApiService.getTestDetails(testId),
            ApiService.getStudentMarksWithoutMissingRecordsInsertion(testId, [batchId])
          ]);
        }

        setHasEditPermission(hasPermission);
        if (!locked) {
          return Promise.all([
            ApiService.getTestDetails(testId),
            ApiService.getStudentMarks(testId, [batchId])
          ]);
        }
        else if (locked && isOnlineTest) {
          return Promise.all([
            ApiService.getTestDetails(testId),
            ApiService.getStudentMarksWithoutMissingRecordsInsertion(testId, [batchId])
          ]);
        }
        return null;
      })
      .then((results) => {
        if (!results) return;
        const [testDetailsResp, studentMarksResp] = results;
        const testSections = testDetailsResp.data?.testSections || [];
        const students = studentMarksResp.data?.data || [];
        setStudents(students);
        setTestSections(testSections);
        const leafSections = testSections.filter(sec => sec.isLeafNode);
        setLeafSections(leafSections);
        const allSectionIds = Array.from(
          new Set(
            students.flatMap((row) => Object.keys(row.sections || {}))
          )
        );
        const leafSectionIds = testSections
          .filter(sec => sec.isLeafNode)
          .map(sec => String(sec.id));
        const sectionMeta = allSectionIds
          .filter(id => leafSectionIds.includes(id))
          .map((id) => {
            const studentWithSection = students.find((row) => row.sections && row.sections[id]);
            const sectionDef = testSections.find(sec => String(sec.id) === id);
            if (studentWithSection) {
              return {
                id,
                name: sectionDef ? sectionDef.name : `Section ${id}`,
                totalMarks: studentWithSection.sections[id].totalMarks || "-",
                threshold: studentWithSection.sections[id].threshold || 0,
              };
            }
            return { id, name: sectionDef ? sectionDef.name : `Section ${id}`, totalMarks: "-", threshold: 0 };
          });
        setSections(sectionMeta);
        setTestMeta(testDetailsResp.data?.testBasicInfo || null);
        const totalsObj = {};
        students.forEach((student) => {
          let total = 0;
          allSectionIds.forEach((sid) => {
            const marks = student.sections?.[sid]?.marks;
            if (!isNaN(parseFloat(marks))) total += parseFloat(marks);
          });
          totalsObj[student.enrollmentId] = total;
        });
        setTotals(totalsObj);
      })
      .catch(() => {
        setError("Failed to fetch marks or test details.");
      })
      .finally(() => setLoading(false));
  }, [testId, batchId, isViewOnlyMode]);

  const handleCellBlur = async ({ enrollmentId, sectionId, type, value }) => {
    const student = students.find(s => s.enrollmentId === enrollmentId);
    const studentTestSectionId = student && student.sections && student.sections[sectionId] && student.sections[sectionId].studentTestSectionId;
    const key = `${enrollmentId}_${sectionId}_${type}`;
    if (type === "marks" && value === "") {
      setCellStatus((prev) => ({ ...prev, [key]: { status: "error", msg: "* Blank" } }));
      return;
    }
    setCellStatus((prev) => ({ ...prev, [key]: { status: "updating", msg: "Updating..." } }));
    try {
      const resp = await ApiService.updateDataEntry({ id: studentTestSectionId, type, value });
      if (resp.data.result === "0") {
        setCellStatus((prev) => ({ ...prev, [key]: { status: "error", msg: resp.data.msg } }));
      } else if (resp.data.result === "1") {
        setCellStatus((prev) => ({ ...prev, [key]: { status: "success", msg: resp.data.msg } }));
        setTimeout(() => {
          setCellStatus((prev) => ({ ...prev, [key]: { status: "", msg: "" } }));
        }, 1000);

        // The backend marks-update strategy already sets status=1 (DATA_AVAILABLE)
        // when the value is numeric, so a separate type:"status" call is unnecessary
        // (and is a no-op anyway — the Java endpoint only handles marks/remarks).
        let parentUpdate = null;
        if (type === "marks") {
          // If no leaves remain ABSENT after this update, recalculate parent and clear ABSENT.
          const leafSection = testSections.find(sec => String(sec.id) === String(sectionId));
          const parentId = leafSection?.parentId;
          const parentTestSectionId = parentId
            ? student?.sections?.[parentId]?.studentTestSectionId
            : null;
          if (parentTestSectionId) {
            const anyAbsent = leafSections.some(sec => {
              if (String(sec.id) === String(sectionId)) return false;
              const data = student.sections[sec.id] || {};
              return Number(data.status) === -1;
            });
            if (!anyAbsent) {
              const total = leafSections.reduce((sum, sec) => {
                if (String(sec.id) === String(sectionId)) {
                  return sum + (isNaN(Number(value)) ? 0 : Number(value));
                }
                const data = student.sections[sec.id] || {};
                const m = data.marks;
                return sum + (isNaN(Number(m)) ? 0 : Number(m));
              }, 0);
              parentUpdate = { parentId, total };
              try {
                await ApiService.updateDataEntry({ id: parentTestSectionId, type: "marks", value: total });
              } catch (e) {
                console.error("Failed to update parent section:", e);
              }
            }
          }
        }

        setStudents((prev) => prev.map(s => {
          if (s.enrollmentId !== enrollmentId) return s;
          if (type === "marks") {
            const newSections = {
              ...s.sections,
              [sectionId]: {
                ...s.sections[sectionId],
                marks: value,
                status: 1
              }
            };
            if (parentUpdate && newSections[parentUpdate.parentId]) {
              newSections[parentUpdate.parentId] = {
                ...newSections[parentUpdate.parentId],
                marks: parentUpdate.total,
                status: 1
              };
            }
            return { ...s, sections: newSections };
          } else if (type === "remarks1" || type === "remarks2") {
            return {
              ...s,
              [type]: value
            };
          }
          return s;
        }));
      }
    } catch {
      setCellStatus((prev) => ({ ...prev, [key]: { status: "error", msg: "Error updating" } }));
    }
  };

  const handleMarkChange = (enrollmentId, sectionId, value, threshold, totalMarks) => {
    setStudents((prev) =>
      prev.map((student) => {
        if (student.enrollmentId !== enrollmentId) return student;
        const updatedSections = {
          ...student.sections,
          [sectionId]: {
            ...student.sections[sectionId],
            marks: value,
            studentGrade: ""
          }
        };
        parentSectionIds.forEach(parentId => {
          const childSections = testSections.filter(sec => String(sec.parentId) === parentId);
          let total = 0;
          childSections.forEach(child => {
            const marks = updatedSections[child.id]?.marks;
            if (!isNaN(parseFloat(marks))) total += parseFloat(marks);
          });
          if (updatedSections[parentId]) {
            updatedSections[parentId] = {
              ...updatedSections[parentId],
              marks: total
            };
          }
        });
        return { ...student, sections: updatedSections };
      })
    );
  };

  const handleMarkAbsent = async ({ enrollmentId, sectionId }) => {
    const student = students.find(s => s.enrollmentId === enrollmentId);
    const studentTestSectionId = student && student.sections && student.sections[sectionId] && student.sections[sectionId].studentTestSectionId;
    const key = `${enrollmentId}_${sectionId}_marks`;
    setCellStatus((prev) => ({ ...prev, [key]: { status: "updating", msg: "Updating..." } }));
    try {
      // Backend strategy StudentTestSectionAbsentUpdate sets marks=0, status=-1, grade=""
      // when value is "ABS"/"ABSENT"/"A". A separate type:"status" call is a no-op server-side.
      const resp = await ApiService.updateDataEntry({ id: studentTestSectionId, type: "marks", value: "ABSENT" });
      if (resp.data.result === "0") {
        setCellStatus((prev) => ({ ...prev, [key]: { status: "error", msg: resp.data.msg } }));
        return;
      }

      // Cascade ABSENT to the parent section so totals/refresh show ABSENT
      const leafSection = testSections.find(sec => String(sec.id) === String(sectionId));
      const parentId = leafSection?.parentId;
      const parentTestSectionId = parentId
        ? student?.sections?.[parentId]?.studentTestSectionId
        : null;
      if (parentTestSectionId) {
        try {
          await ApiService.updateDataEntry({ id: parentTestSectionId, type: "marks", value: "ABSENT" });
        } catch (e) {
          console.error("Failed to cascade ABSENT to parent section:", e);
        }
      }

      setCellStatus((prev) => ({ ...prev, [key]: { status: "success", msg: resp.data.msg || "Saved" } }));
      setTimeout(() => {
        setCellStatus((prev) => ({ ...prev, [key]: { status: "", msg: "" } }));
      }, 1000);
      setStudents((prev) => prev.map(s => {
        if (s.enrollmentId !== enrollmentId) return s;
        const newSections = {
          ...s.sections,
          [sectionId]: {
            ...s.sections[sectionId],
            marks: 0,
            status: -1,
            studentGrade: ""
          }
        };
        if (parentId && newSections[parentId]) {
          newSections[parentId] = {
            ...newSections[parentId],
            marks: 0,
            status: -1
          };
        }
        return { ...s, sections: newSections };
      }));
    } catch {
      setCellStatus((prev) => ({ ...prev, [key]: { status: "error", msg: "Error updating" } }));
    }
  };

  const handleGradeChange = (enrollmentId, sectionId, value) => {
    setStudents((prev) =>
      prev.map((student) => {
        if (student.enrollmentId !== enrollmentId) return student;
        return {
          ...student,
          sections: {
            ...student.sections,
            [sectionId]: {
              ...student.sections[sectionId],
              studentGrade: value,
              marks: ""
            }
          }
        };
      })
    );
  };

  const handleRemarksBlur = (enrollmentId, field, value) => {
    setStudents((prev) =>
      prev.map((student) =>
        student.enrollmentId === enrollmentId
          ? { ...student, [field]: value }
          : student
      )
    );
  };

  const sortedSections = [
    ...sections.filter(section => !parentSectionIds.includes(String(section.id))),
    ...sections.filter(section => parentSectionIds.includes(String(section.id)))
  ];

  const testStatuses = testLockConfig?.testStatuses || {};
  const sortedStudents = [...students].sort((a, b) => (a.index || 0) - (b.index || 0));

  const studentCodeEnabled = testLockConfig?.studentCodeEnabled;
  const studentNameEnabled = testLockConfig?.studentNameEnabled;
  const hallTicketNumberEnabled = testLockConfig?.hallTicketNumberEnabled;
  const principalRemarksEnabled = testLockConfig?.principalRemarksEnabled;
  const teacherRemarksEnabled = testLockConfig?.teacherRemarksEnabled;
  const principalRemarksHeader = testLockConfig?.principalRemarksHeader;
  const teacherRemarksHeader = testLockConfig?.teacherRemarksHeader;
  const parentSectionId = (testSections.find(sec => !sec.isLeafNode) || leafSections[0])?.id;

  const handleExcelExport = () => {
    const headers = ['S.No.'];
    if (studentCodeEnabled) headers.push('Student Code');
    if (studentNameEnabled) headers.push('Name');
    if (hallTicketNumberEnabled) headers.push('Hall Ticket');

    leafSections.forEach(section => {
      const sectionMeta = sections.find(s => s.id === String(section.id));
      headers.push(`${sectionMeta?.name || section.name || `Section ${section.id}`} (${section.totalMarks} max)`);
    });
    headers.push('Total');
    if (principalRemarksEnabled) headers.push(principalRemarksHeader || 'Principal Remarks');
    if (teacherRemarksEnabled) headers.push(teacherRemarksHeader || 'Teacher Remarks');

    const rows = sortedStudents.map((student, idx) => {
      const row = [idx + 1];
      if (studentCodeEnabled) row.push(student.studentCode || '');
      if (studentNameEnabled) row.push(student.studentName || '');
      if (hallTicketNumberEnabled) row.push(student.hallTicketNumber || '');

      leafSections.forEach(section => {
        const secData = student.sections[section.id] || {};
        const statusNum = secData.status === null || secData.status === undefined || secData.status === ""
          ? 0
          : Number(secData.status);
        let value = '';
        if (statusNum === -1) {
          value = 'ABSENT';
        } else if (secData.marks !== undefined && secData.marks !== null && secData.marks !== '') {
          value = secData.marks;
        } else {
          value = testStatuses[secData.status] || (secData.status !== undefined ? secData.status : '-');
        }
        if (secData.remarks2) {
          value = `${value} (${secData.remarks2})`;
        }
        row.push(value);
      });

      const anyAbsent = leafSections.some((sec) => {
        const data = student.sections[sec.id] || {};
        return Number(data.status) === -1;
      });
      const total = anyAbsent ? "ABSENT" : leafSections.reduce((sum, section) => {
        const secData = student.sections[section.id] || {};
        const statusNum = secData.status === null || secData.status === undefined || secData.status === ""
          ? 0
          : Number(secData.status);
        if (statusNum === 1 || statusNum === 0) {
          const marks = secData.marks;
          return sum + (isNaN(Number(marks)) ? 0 : Number(marks));
        }
        return sum;
      }, 0);
      row.push(total);

      if (principalRemarksEnabled) row.push(student.remarks1 || '');
      if (teacherRemarksEnabled) row.push(student.remarks2 || '');

      return row;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    const colWidths = [{ wch: 6 }];
    if (studentCodeEnabled) colWidths.push({ wch: 15 });
    if (studentNameEnabled) colWidths.push({ wch: 25 });
    if (hallTicketNumberEnabled) colWidths.push({ wch: 15 });
    leafSections.forEach(() => colWidths.push({ wch: 15 }));
    colWidths.push({ wch: 10 });
    if (principalRemarksEnabled) colWidths.push({ wch: 30 });
    if (teacherRemarksEnabled) colWidths.push({ wch: 30 });
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Test Marks');

    const testName = testMeta?.name || 'Test';
    const sanitizedTestName = testName.replace(/[^a-zA-Z0-9]/g, '-');
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `${sanitizedTestName}-Batch-${batchId}-${date}.xlsx`);
  };

  if (loading) {
    return (
      <div className="sm-page">
        <div className="sm-loading">
          <div className="sm-spinner" aria-hidden="true" />
          <span>Loading marks…</span>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="sm-page">
        <div className="sm-error-card">
          <FontAwesomeIcon icon={faTimesCircle} />
          <span>{error}</span>
        </div>
      </div>
    );
  }
  if (!isViewOnlyMode && lockStatus && !isOnlineTest) {
    return (
      <div className="sm-page">
        <header className="sm-header">
          {onClose && (
            <button
              type="button"
              className="sm-back-btn"
              onClick={onClose}
              aria-label="Go back"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}
          <div className="sm-header-text">
            <span className="sm-header-eyebrow">Test Data Entry</span>
            <h1 className="sm-header-title">Locked</h1>
          </div>
        </header>
        <div className="sm-body">
          <div className="sm-banner sm-banner-error">
            <FontAwesomeIcon icon={faTimesCircle} className="sm-banner-icon" />
            <span>{lockMessage}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sm-page">
      <header className="sm-header">
        {onClose && (
          <button
            type="button"
            className="sm-back-btn"
            onClick={onClose}
            aria-label="Go back"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        <div className="sm-header-text">
          <span className="sm-header-eyebrow">
            {isViewOnlyMode ? "Test Analysis" : "Test Data Entry"}
          </span>
          <h1 className="sm-header-title">{testMeta?.name || "Marks"}</h1>
        </div>
        <div className="sm-header-actions">
          {isViewOnlyMode && students.length > 0 && (
            <button
              type="button"
              className="sm-btn sm-btn-success"
              onClick={handleExcelExport}
            >
              <FontAwesomeIcon icon={faFileExcel} />
              Download Excel
            </button>
          )}
        </div>
      </header>

      <div className="sm-body">
        {!hasEditPermission && (
          <div className={`sm-banner ${isViewOnlyMode ? "sm-banner-info" : "sm-banner-warn"}`}>
            <FontAwesomeIcon
              icon={isViewOnlyMode ? faInfoCircle : faExclamationTriangle}
              className="sm-banner-icon"
            />
            <span>
              {isViewOnlyMode
                ? "Test Analysis View-Only Mode: This page is for viewing marks only. Editing is not available."
                : "View-Only Mode: You do not have permission to edit test data entries."}
            </span>
          </div>
        )}

        {testMeta && (
          <div className="sm-meta">
            <div className="sm-meta-item">
              <span className="sm-meta-label">Date</span>
              <span className="sm-meta-value">{testMeta.testDate || "-"}</span>
            </div>
            <div className="sm-meta-item">
              <span className="sm-meta-label">Total Marks</span>
              <span className="sm-meta-value">{testMeta.totalMarks || "-"}</span>
            </div>
            <div className="sm-meta-item">
              <span className="sm-meta-label">Category</span>
              <span className="sm-meta-value">{testMeta.keyword || "-"}</span>
            </div>
            <div className="sm-meta-item">
              <span className="sm-meta-label">Test Type</span>
              <span className="sm-meta-value">{testMeta.testType || "-"}</span>
            </div>
            <div className="sm-meta-spacer" />
            <span className="sm-student-count">
              <FontAwesomeIcon icon={faUsers} />
              {sortedStudents.length} {sortedStudents.length === 1 ? "Student" : "Students"}
            </span>
          </div>
        )}

        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th className="sm-cell-sno">S.No.</th>
                {studentCodeEnabled && <th>Student Code</th>}
                {studentNameEnabled && <th>Name</th>}
                {hallTicketNumberEnabled && <th>Hall Ticket</th>}
                {leafSections.map((section) => (
                  <th key={section.id}>
                    {sections.find(s => s.id === String(section.id))?.name || section.name || `Section ${section.id}`}
                    <span className="sm-th-max">{section.totalMarks} max</span>
                  </th>
                ))}
                <th>Total</th>
                {principalRemarksEnabled && <th>{principalRemarksHeader}</th>}
                {teacherRemarksEnabled && <th>{teacherRemarksHeader}</th>}
              </tr>
            </thead>
            <tbody>
              {sortedStudents.map((student, idx) => (
                <tr key={student.enrollmentId}>
                  <td className="sm-cell-sno">{idx + 1}</td>
                  {studentCodeEnabled && <td>{student.studentCode}</td>}
                  {studentNameEnabled && <td className="sm-cell-name">{student.studentName}</td>}
                  {hallTicketNumberEnabled && <td>{student.hallTicketNumber}</td>}
                  {leafSections.map((section) => {
                    const secData = student.sections[section.id] || {};
                    const statusNum = secData.status === null || secData.status === undefined || secData.status === ""
                      ? 0
                      : Number(secData.status);
                    const isSavedAbsent = statusNum === -1 && typeof secData.marks === "number";
                    const marks = isSavedAbsent
                      ? "ABSENT"
                      : (secData.marks === undefined ? "" : secData.marks);
                    const key = `${student.enrollmentId}_${section.id}_marks`;
                    const cs = cellStatus[key];
                    const csClass = cs?.status === "error"
                      ? "is-error"
                      : cs?.status === "success"
                        ? "is-success"
                        : cs?.status === "updating"
                          ? "is-updating"
                          : "";

                    if (!hasEditPermission) {
                      return (
                        <td key={section.id}>
                          <div className="sm-cell-marks-display">
                            {statusNum === -1
                              ? "ABSENT"
                              : (secData.marks !== undefined && secData.marks !== null && secData.marks !== ''
                                ? secData.marks
                                : (testStatuses[secData.status] || (secData.status !== undefined ? secData.status : '-')))}
                          </div>
                        </td>
                      );
                    }

                    if (statusNum === 1 || statusNum === 0 || statusNum === -1) {
                      return (
                        <td key={section.id}>
                          <input
                            type="text"
                            inputMode="text"
                            value={marks}
                            ref={el => { inputRefs.current[key] = el; }}
                            onChange={(e) => {
                              const value = e.target.value;
                              const trimmed = value.trim();
                              const isNumeric = /^-?\d+(\.\d+)?$/.test(trimmed);
                              const numericInRange = isNumeric && Number(trimmed) >= 0 && Number(trimmed) <= section.totalMarks;
                              const isAbsentLike = trimmed === "" || isAbsentPrefix(trimmed);
                              if (value !== "" && !numericInRange && !isAbsentLike) {
                                setInvalidCells(prev => ({ ...prev, [key]: true }));
                              } else {
                                setInvalidCells(prev => ({ ...prev, [key]: false }));
                              }
                              handleMarkChange(
                                student.enrollmentId,
                                section.id,
                                value,
                                section.threshold,
                                section.totalMarks
                              );
                            }}
                            onBlur={(e) => {
                              const raw = e.target.value;
                              if (isAbsentToken(raw)) {
                                setInvalidCells(prev => ({ ...prev, [key]: false }));
                                handleMarkAbsent({
                                  enrollmentId: student.enrollmentId,
                                  sectionId: section.id
                                });
                                return;
                              }
                              const trimmed = raw.trim();
                              const isNumeric = trimmed !== "" && /^-?\d+(\.\d+)?$/.test(trimmed);
                              if (raw !== "" && (!isNumeric || Number(trimmed) > section.totalMarks || Number(trimmed) < 0)) {
                                setInvalidCells(prev => ({ ...prev, [key]: true }));
                                setTimeout(() => {
                                  if (inputRefs.current[key]) inputRefs.current[key].focus();
                                }, 10);
                              } else {
                                setInvalidCells(prev => ({ ...prev, [key]: false }));
                                handleCellBlur({
                                  enrollmentId: student.enrollmentId,
                                  sectionId: section.id,
                                  type: "marks",
                                  value: raw
                                });
                              }
                            }}
                            className={`sm-input ${invalidCells[key] ? "is-invalid" : ""}`}
                          />
                          <div className={`sm-cell-msg ${invalidCells[key] ? "is-error" : csClass}`}>
                            {invalidCells[key] ? `Enter 0–${section.totalMarks} or A/ABS/ABSENT` : cs?.msg}
                          </div>
                        </td>
                      );
                    } else {
                      return (
                        <td key={section.id}>
                          <span className="sm-cell-readonly">
                            {secData.marks !== undefined && secData.marks !== null && secData.marks !== ''
                              ? secData.marks
                              : (testStatuses[secData.status] || secData.status)}
                          </span>
                        </td>
                      );
                    }
                  })}
                  <td>
                    <span className="sm-cell-total">
                      {(() => {
                        const anyAbsent = leafSections.some((sec) => {
                          const data = student.sections[sec.id] || {};
                          return Number(data.status) === -1;
                        });
                        if (anyAbsent) return "ABSENT";
                        return leafSections.reduce((sum, section) => {
                          const secData = student.sections[section.id] || {};
                          const statusNum = secData.status === null || secData.status === undefined || secData.status === ""
                            ? 0
                            : Number(secData.status);
                          if (statusNum === 1 || statusNum === 0) {
                            const marks = secData.marks;
                            return sum + (isNaN(Number(marks)) ? 0 : Number(marks));
                          }
                          return sum;
                        }, 0);
                      })()}
                    </span>
                  </td>
                  {principalRemarksEnabled && (
                    <td>
                      {!hasEditPermission ? (
                        <div className="sm-cell-remarks-display">
                          {student.remarks1 || '-'}
                        </div>
                      ) : (
                        <>
                          <textarea
                            value={student.remarks1 || ""}
                            onChange={(e) => {
                              handleRemarksBlur(
                                student.enrollmentId,
                                "remarks1",
                                e.target.value
                              );
                            }}
                            onBlur={(e) => {
                              handleCellBlur({
                                enrollmentId: student.enrollmentId,
                                sectionId: parentSectionId,
                                type: "remarks1",
                                value: e.target.value
                              });
                            }}
                            className="sm-input sm-textarea"
                            aria-label={principalRemarksHeader}
                            rows={3}
                          />
                          <div className={`sm-cell-msg ${cellStatus[`${student.enrollmentId}_${parentSectionId}_remarks1`]?.status === "error" ? "is-error" : cellStatus[`${student.enrollmentId}_${parentSectionId}_remarks1`]?.status === "success" ? "is-success" : ""}`}>
                            {cellStatus[`${student.enrollmentId}_${parentSectionId}_remarks1`]?.msg}
                          </div>
                        </>
                      )}
                    </td>
                  )}
                  {teacherRemarksEnabled && (
                    <td>
                      {!hasEditPermission ? (
                        <div className="sm-cell-remarks-display">
                          {student.remarks2 || '-'}
                        </div>
                      ) : (
                        <>
                          <textarea
                            value={student.remarks2 || ""}
                            onChange={(e) => {
                              handleRemarksBlur(
                                student.enrollmentId,
                                "remarks2",
                                e.target.value
                              );
                            }}
                            onBlur={(e) => {
                              handleCellBlur({
                                enrollmentId: student.enrollmentId,
                                sectionId: parentSectionId,
                                type: "remarks2",
                                value: e.target.value
                              });
                            }}
                            className="sm-input sm-textarea"
                            aria-label={teacherRemarksHeader}
                            rows={3}
                          />
                          <div className={`sm-cell-msg ${cellStatus[`${student.enrollmentId}_${parentSectionId}_remarks2`]?.status === "error" ? "is-error" : cellStatus[`${student.enrollmentId}_${parentSectionId}_remarks2`]?.status === "success" ? "is-success" : ""}`}>
                            {cellStatus[`${student.enrollmentId}_${parentSectionId}_remarks2`]?.msg}
                          </div>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
